/**
 * Super Agent System Type Definitions
 */

// Specialist Agent Type
export type SpecialistAgentType =
  | 'marketing'
  | 'image_generation'
  | 'data_analysis'
  | 'copywriting'
  | 'seo';

// Execution Status (Async version)
export type ExecutionStatus =
  | 'pending'   // Queued, waiting to be processed
  | 'running'   // Currently being processed
  | 'completed' // Successfully completed
  | 'failed';   // Failed to complete

// Processing Step (for detailed progress tracking)
export type ProcessingStep =
  | 'analyzing'    // Analyzing request and selecting agents
  | 'executing'    // Agents are running
  | 'aggregating'  // Aggregating results
  | 'completed'    // All done
  | 'failed';      // Failed

// Agent Result Status
export type AgentResultStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed';

/**
 * Specialist Agent Definition
 */
export interface SpecialistAgent {
  id: number;
  name: string;
  type: SpecialistAgentType;
  description: string;
  model_type: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Super Agent Execution
 */
export interface SuperAgentExecution {
  id: number;
  user_id: number;
  chat_id?: number;
  user_message: string;
  selected_agents: SpecialistAgentType[];
  execution_status: ExecutionStatus;
  started_at: string;
  completed_at?: string;
  final_response?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Agent Result
 */
export interface AgentResult {
  id: number;
  execution_id: number;
  agent_type: SpecialistAgentType;
  agent_name: string;
  agent_prompt: string;
  agent_response: string;
  execution_time_ms: number;
  status: AgentResultStatus;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Execution with Results
 */
export interface ExecutionWithResults extends SuperAgentExecution {
  results: AgentResult[];
}

/**
 * API Response Types
 */

export interface ProcessRequestPayload {
  user_request: string;
  max_agents?: number;
}

/**
 * Async Process Request Response (202 Accepted)
 * Returned immediately when a request is queued
 */
export interface SuperAgentAsyncResponse {
  execution_id: number;
  status: ExecutionStatus;
  message: string;
}

/**
 * Status Response (for polling)
 * Contains progress information and current state
 */
export interface SuperAgentStatusResponse {
  execution_id: number;
  execution_status: ExecutionStatus;
  current_step?: ProcessingStep;
  step_detail?: string;
  progress: {
    total: number;
    completed: number;
    running: number;
    pending: number;
    failed: number;
    percentage: number;
  };
  // Only available when status is 'completed' or 'failed'
  selected_agents?: Array<{
    id: number;
    agent_type: SpecialistAgentType;
    agent_name: string;
    status: string;
    execution_time_ms: number;
  }> | SpecialistAgentType[];
  results?: AgentResult[];
  final_response?: string;
  metadata?: {
    total_agents: number;
    successful_agents: number;
    failed_agents: number;
    success_rate: number;
    total_execution_time?: number;
  };
  statistics?: {
    total_requests: number;
    avg_execution_time: number;
    success_rate: number;
  };
}

/**
 * Legacy: Process Request Response (sync version - deprecated)
 */
export interface ProcessRequestResponse {
  success: boolean;
  data: {
    execution_id: number;
    execution_status: ExecutionStatus;
    selected_agents: SpecialistAgent[];
    results: AgentResult[];
    final_response?: string;
    total_execution_time?: number;
  };
  error?: string;
}

export interface GetExecutionDetailsResponse {
  success: boolean;
  data: {
    execution: SuperAgentExecution;
    results: AgentResult[];
    response?: string;
    metadata?: {
      total_agents: number;
      successful_agents: number;
      failed_agents: number;
      success_rate: number;
    };
  };
  error?: string;
}

export interface GetAvailableAgentsResponse {
  success: boolean;
  data: SpecialistAgent[];
  error?: string;
}

export interface GetUserExecutionsResponse {
  success: boolean;
  data: Array<{
    id: number;
    request_summary: string;
    execution_status: ExecutionStatus;
    started_at: string;
    completed_at?: string;
  }>;
  error?: string;
}

export interface GetStatisticsResponse {
  success: boolean;
  data: {
    total_executions: number;
    completed_executions: number;
    failed_executions: number;
    total_agent_invocations: number;
    available_agents: number;
  };
  error?: string;
}

/**
 * UI State Types
 */

export interface SuperAgentUIState {
  isProcessing: boolean;
  executionId?: number;
  executionStatus?: ExecutionStatus;
  progress?: {
    total: number;
    completed: number;
    running: number;
    pending: number;
    failed: number;
    percentage: number;
  };
  currentExecution?: SuperAgentExecution;
  agentResults: Map<SpecialistAgentType, AgentResult>;
  finalResponse?: string;
  error?: string;
}

/**
 * Agent Metadata for UI Display
 */
export interface AgentMetadata {
  type: SpecialistAgentType;
  name: string;
  icon: string;
  color: string;
  description: string;
}

export const AGENT_METADATA: Record<SpecialistAgentType, AgentMetadata> = {
  marketing: {
    type: 'marketing',
    name: 'Marketing Specialist',
    icon: '📊',
    color: '#3b82f6',
    description: 'Marketing strategy and campaign planning expert'
  },
  image_generation: {
    type: 'image_generation',
    name: 'Image Generation Specialist',
    icon: '🎨',
    color: '#8b5cf6',
    description: 'AI-powered image generation and visual content creation'
  },
  data_analysis: {
    type: 'data_analysis',
    name: 'Data Analysis Specialist',
    icon: '📈',
    color: '#10b981',
    description: 'Data analysis and insights extraction expert'
  },
  copywriting: {
    type: 'copywriting',
    name: 'Copywriting Specialist',
    icon: '✍️',
    color: '#f59e0b',
    description: 'Creative copywriting and content creation expert'
  },
  seo: {
    type: 'seo',
    name: 'SEO Specialist',
    icon: '🔍',
    color: '#ef4444',
    description: 'Search engine optimization and content strategy expert'
  }
};
